import type {
  KryspaceRenderManifest,
  RenderManifestQueueItem,
} from '../../../types/kryspaceRenderManifest';

export type CrossDomainMode = 'capture' | 'respond' | 'log';

export const CROSS_DOMAIN_MODES: CrossDomainMode[] = ['capture', 'respond', 'log'];

export const CROSS_DOMAIN_MODE_LABELS: Record<CrossDomainMode, string> = {
  capture: 'Capture',
  respond: 'Respond',
  log: 'Log',
};

export interface CrossDomainAttentionModel {
  modeDefault: CrossDomainMode;
  capacityHeadline: string;
  profileComplete: boolean;
  queue: RenderManifestQueueItem[];
}

export function parseCrossDomainAttention(
  manifest: KryspaceRenderManifest
): CrossDomainAttentionModel | null {
  const section = manifest.sections.find((s) => s.id === 'cross-domain-attention');
  if (!section) return null;
  const meta = section.meta ?? {};
  const modeDefault = normalizeMode(meta.modeDefault);
  return {
    modeDefault,
    capacityHeadline: String(meta.capacityHeadline ?? '').trim(),
    profileComplete: Boolean(meta.profileComplete),
    queue: [...(section.queue ?? [])],
  };
}

export function normalizeMode(value: unknown): CrossDomainMode {
  const raw = String(value ?? '').trim().toLowerCase();
  if (raw === 'capture' || raw === 'respond' || raw === 'log') return raw;
  return 'respond';
}

/** Mode query filter — heuristic until triageRoute derivation (P2). */
export function filterCrossDomainQueue(
  queue: RenderManifestQueueItem[],
  mode: CrossDomainMode
): RenderManifestQueueItem[] {
  if (mode === 'capture') {
    return queue.filter((item) => /active|ready|drafted|initialized/i.test(item.statusPhrase ?? ''));
  }
  if (mode === 'respond') {
    return queue.filter(
      (item) =>
        item.recommended === true ||
        /^(P0|P1)$/i.test(String(item.priority ?? '').trim())
    );
  }
  return queue.filter((item) => /defer|block|log|park/i.test(item.statusPhrase ?? ''));
}

export function previewCrossDomainQueue(
  queue: RenderManifestQueueItem[],
  mode: CrossDomainMode,
  limit = 3
): RenderManifestQueueItem[] {
  const filtered = filterCrossDomainQueue(queue, mode);
  const source = filtered.length > 0 ? filtered : queue;
  return source.slice(0, limit);
}
